import type { ProviderResult } from '@/lib/pricing'
import { fmtUSD } from '@/lib/pricing'
import {Zap, Activity, Layers3} from 'lucide-react'

interface Props {
  results: ProviderResult[]
}

function Hero({ results }: Props) {
  const cheapest = results[0]

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 cosmic-grid opacity-60 pointer-events-none"></div>
      <div className="absolute inset-0 starfield opacity-50 pointer-events-none"></div>
      <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-cyan-500/[0.07] blur-3xl rounded-full pointer-events-none"></div>

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-12 md:pt-16 pb-10">
        <div className="flex flex-col items-center text-center">
          <span className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-cyan-300/90 bg-cyan-500/[0.06] border border-cyan-500/20 rounded-full px-3 py-1 mb-6">
            <span className="w-1 h-1 rounded-full bg-cyan-400 pulse-ring"></span>
            AWS · Azure · GCP · Oracle · Live comparison
          </span>

          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.05] max-w-4xl">
            <span className="gradient-text">Multi-Cloud Pricing</span>
            <br />
            <span className="text-slate-100">Calculator built for FinOps speed.</span>
          </h1>

          <p className="mt-5 text-[15px] md:text-base text-slate-400 max-w-2xl leading-relaxed">
            Model AWS EC2, Azure Storage, Google BigQuery and Oracle OCI workloads against a single set of inputs. Get a transparent multi-cloud cost matrix — without waiting for native calculators.
          </p>

          <div className="mt-7 flex flex-wrap items-center justify-center gap-2 text-[11px] font-mono text-slate-500">
            <span className="px-2.5 py-1 rounded-full border border-[#1F2A44]">AWS Pricing Calculator</span>
            <span className="px-2.5 py-1 rounded-full border border-[#1F2A44]">Google Cloud Pricing Calculator</span>
            <span className="px-2.5 py-1 rounded-full border border-[#1F2A44]">Azure Storage Cost Calculator</span>
            <span className="px-2.5 py-1 rounded-full border border-[#1F2A44]">GCP Cost Estimator</span>
          </div>

          {/* Live stats strip */}
          <div className="mt-9 grid grid-cols-3 gap-px bg-[#1F2A44] border border-[#1F2A44] rounded-xl overflow-hidden w-full max-w-3xl">
            <div className="bg-[#0E1525] px-4 py-4">
              <div className="flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
                <Zap className="w-3 h-3" /> Cheapest now
              </div>
              <div className="font-mono text-xl md:text-2xl font-bold text-emerald-300 tabular-nums">{fmtUSD(cheapest.total)}</div>
              <div className="text-[10px] text-slate-500 mt-1">{cheapest.provider.shortName} · monthly</div>
            </div>
            <div className="bg-[#0E1525] px-4 py-4">
              <div className="flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
                <Activity className="w-3 h-3" /> Calc latency
              </div>
              <div className="font-mono text-xl md:text-2xl font-bold text-cyan-300 tabular-nums">&lt;1<span className="text-slate-500">ms</span></div>
              <div className="text-[10px] text-slate-500 mt-1">Client-side · zero round-trips</div>
            </div>
            <div className="bg-[#0E1525] px-4 py-4">
              <div className="flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
                <Layers3 className="w-3 h-3" /> Providers
              </div>
              <div className="font-mono text-xl md:text-2xl font-bold text-slate-100 tabular-nums">4</div>
              <div className="text-[10px] text-slate-500 mt-1">AWS · Azure · GCP · OCI</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Hero
